import React, { useState, useEffect } from 'react';
import './PaymentSuccessModal.css';

/**
 * Payment Success Modal - Cyberpunk Edition
 * A stunning, animated payment confirmation modal
 */
const PaymentSuccessModal = ({ isOpen, paymentData, planData, onClose }) => {
  const [downloading, setDownloading] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [animationPhase, setAnimationPhase] = useState(0);

  useEffect(() => {
    if (isOpen) {
      // Trigger animations in sequence
      setAnimationPhase(1);
      setTimeout(() => setAnimationPhase(2), 300);
      setTimeout(() => setAnimationPhase(3), 600);
      setTimeout(() => setShowConfetti(true), 800);
      setTimeout(() => setAnimationPhase(4), 1000);
    } else {
      setAnimationPhase(0);
      setShowConfetti(false);
    }
  }, [isOpen]);

  const handleDownloadReceipt = async () => {
    if (!paymentData?.receipt_url && !paymentData?.transaction_id) return;
    
    setDownloading(true);
    
    // Simulate download delay for better UX
    setTimeout(() => {
      const receiptContent = `
╔══════════════════════════════════════════════════════════════╗
║                    KAMELOT RECEIPT                        ║
╠══════════════════════════════════════════════════════════════╣
║ Transaction ID: ${(paymentData.transaction_id || 'N/A').padEnd(47)}║
║ Date:          ${new Date().toLocaleDateString().padEnd(47)}║
║ Time:          ${new Date().toLocaleTimeString().padEnd(47)}║
╠══════════════════════════════════════════════════════════════╣
║ PLAN DETAILS                                                 ║
╠══════════════════════════════════════════════════════════════╣
║ Plan:          ${(planData?.name || 'N/A').padEnd(47)}║
║ Amount:        ${('GH₵' + (planData?.price || '0.00')).padEnd(47)}║
║ Status:        ${'COMPLETED ✓'.padEnd(47)}║
╠══════════════════════════════════════════════════════════════╣
║ Thank you for choosing Kamelot!                           ║
║ For support: support@quantumpay.gh                           ║
╚══════════════════════════════════════════════════════════════╝
      `;

      const blob = new Blob([receiptContent], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Kamelot-Receipt-${paymentData?.transaction_id || Date.now()}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      setDownloading(false);
    }, 1000);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleShare = async () => {
    const shareData = {
      title: 'Payment Successful - Kamelot',
      text: `I just completed a payment of GH₵${planData?.price} for ${planData?.name} plan!`,
      url: window.location.href
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(`${shareData.text} ${shareData.url}`);
      showNotification('Link copied to clipboard!');
    }
  };

  const showNotification = (message) => {
    const notification = document.createElement('div');
    notification.className = 'psm-notification';
    notification.innerHTML = `<i className="fas fa-check-circle"></i><span>${message}</span>`;
    document.body.appendChild(notification);
    setTimeout(() => notification.classList.add('show'), 100);
    setTimeout(() => {
      notification.classList.remove('show');
      setTimeout(() => notification.remove(), 300);
    }, 2500);
  };

  const formatDate = () => {
    return new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = () => {
    return new Date().toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="psm-overlay" onClick={onClose}>
      {/* Animated Background */}
      <div className="psm-bg-effects">
        <div className="psm-grid"></div>
        <div className="psm-glow"></div>
        <div className="psm-particles">
          {[...Array(12)].map((_, i) => (
            <span key={i} className={`psm-particle p${i + 1}`}></span>
          ))}
        </div>
      </div>

      {/* Confetti Effect */}
      {showConfetti && (
        <div className="psm-confetti">
          {[...Array(50)].map((_, i) => (
            <span 
              key={i} 
              className="confetti-piece"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${3 + Math.random() * 2}s`,
                backgroundColor: ['#00ff88', '#00d9ff', '#ffb800', '#ff4757', '#a855f7'][Math.floor(Math.random() * 5)]
              }}
            ></span>
          ))}
        </div>
      )}

      <div className="psm-container" onClick={e => e.stopPropagation()}>
        {/* Success Animation Header */}
        <div className="psm-header">
          <div className={`success-circle ${animationPhase >= 1 ? 'animate' : ''}`}>
            <div className="circle-ring ring-1"></div>
            <div className="circle-ring ring-2"></div>
            <div className="circle-ring ring-3"></div>
            <div className="checkmark">
              <i className="fas fa-check"></i>
            </div>
          </div>
          
          <h2 className={`psm-title ${animationPhase >= 2 ? 'animate' : ''}`}>
            <span className="glitch" data-text="PAYMENT SUCCESSFUL">PAYMENT SUCCESSFUL</span>
          </h2>
          
          <p className={`psm-subtitle ${animationPhase >= 3 ? 'animate' : ''}`}>
            Transaction completed successfully
          </p>
        </div>

        {/* Main Content */}
        <div className={`psm-content ${animationPhase >= 4 ? 'animate' : ''}`}>
          {/* Amount Display */}
          <div className="psm-amount-section">
            <div className="amount-label">Amount Paid</div>
            <div className="amount-value">
              <span className="currency">GH₵</span>
              <span className="number">{planData?.price || '0.00'}</span>
            </div>
            <div className="plan-badge">
              <i className="fas fa-crown"></i>
              {planData?.name || 'Premium'} Plan
            </div>
          </div>

          {/* Transaction Details Card */}
          <div className="psm-details-card">
            <div className="details-header">
              <i className="fas fa-receipt"></i>
              <span>Transaction Details</span>
              <div className="live-indicator">
                <span className="live-dot"></span>
                <span className="live-text">LIVE</span>
              </div>
            </div>

            <div className="details-grid">
              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-hashtag"></i>
                </div>
                <div className="detail-info">
                  <label>Transaction ID</label>
                  <span className="detail-value mono">{paymentData?.transaction_id || 'TXN-' + Date.now()}</span>
                </div>
              </div>

              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-calendar-alt"></i>
                </div>
                <div className="detail-info">
                  <label>Date</label>
                  <span className="detail-value">{formatDate()}</span>
                </div>
              </div>

              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-clock"></i>
                </div>
                <div className="detail-info">
                  <label>Time</label>
                  <span className="detail-value">{formatTime()}</span>
                </div>
              </div>

              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-credit-card"></i>
                </div>
                <div className="detail-info">
                  <label>Payment Method</label>
                  <span className="detail-value">{paymentData?.method || 'Card Payment'}</span>
                </div>
              </div>

              {paymentData?.card_last_four && (
                <div className="detail-item">
                  <div className="detail-icon">
                    <i className="fas fa-lock"></i>
                  </div>
                  <div className="detail-info">
                    <label>Card Used</label>
                    <span className="detail-value mono">**** {paymentData.card_last_four}</span>
                  </div>
                </div>
              )}

              <div className="detail-item highlight">
                <div className="detail-icon success">
                  <i className="fas fa-check-circle"></i>
                </div>
                <div className="detail-info">
                  <label>Status</label>
                  <span className="detail-value status-success">
                    <i className="fas fa-shield-alt"></i>
                    Completed & Verified
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Security Badge */}
          <div className="psm-security-badge">
            <div className="shield-icon">
              <i className="fas fa-shield-alt"></i>
            </div>
            <div className="security-text">
              <span className="security-title">Secured by Kamelot</span>
              <span className="security-subtitle">256-bit SSL Encryption</span>
            </div>
            <div className="security-checks">
              <span><i className="fas fa-check"></i> Encrypted</span>
              <span><i className="fas fa-check"></i> Verified</span>
              <span><i className="fas fa-check"></i> Protected</span>
            </div>
          </div>

          {/* Email Notice */}
          <div className="psm-email-notice">
            <div className="email-icon">
              <i className="fas fa-envelope-open-text"></i>
            </div>
            <p>A detailed receipt has been sent to your email address</p>
          </div>

          {/* Action Buttons */}
          <div className="psm-actions">
            <button 
              className="psm-btn primary"
              onClick={handleDownloadReceipt}
              disabled={downloading}
            >
              <span className="btn-content">
                {downloading ? (
                  <>
                    <i className="fas fa-circle-notch fa-spin"></i>
                    <span>Downloading...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-download"></i>
                    <span>Download Receipt</span>
                  </>
                )}
              </span>
              <div className="btn-shine"></div>
            </button>

            <div className="psm-btn-row">
              <button className="psm-btn secondary" onClick={handlePrint}>
                <i className="fas fa-print"></i>
                <span>Print</span>
              </button>
              
              <button className="psm-btn secondary" onClick={handleShare}>
                <i className="fas fa-share-alt"></i>
                <span>Share</span>
              </button>
            </div>
          </div>

          {/* Done Button */}
          <button className="psm-btn done" onClick={onClose}>
            <i className="fas fa-check-circle"></i>
            <span>Done</span>
          </button>
        </div>

        {/* Footer */}
        <div className="psm-footer">
          <div className="support-link">
            <i className="fas fa-question-circle"></i>
            <span>Need help? <a href="#support">Contact Support</a></span>
          </div>
        </div>
      </div>

      {/* Print-only Receipt */}
      <div className="print-only-receipt">
        <div className="print-content">
          <h1>KAMELOT - PAYMENT RECEIPT</h1>
          <div className="print-divider"></div>
          <div className="print-row">
            <span>Transaction ID:</span>
            <strong>{paymentData?.transaction_id || 'N/A'}</strong>
          </div>
          <div className="print-row">
            <span>Date:</span>
            <strong>{formatDate()}</strong>
          </div>
          <div className="print-row">
            <span>Plan:</span>
            <strong>{planData?.name || 'N/A'}</strong>
          </div>
          <div className="print-row">
            <span>Amount:</span>
            <strong>GH₵{planData?.price || '0.00'}</strong>
          </div>
          <div className="print-row">
            <span>Status:</span>
            <strong>COMPLETED ✓</strong>
          </div>
          <div className="print-divider"></div>
          <p className="print-thanks">Thank you for your business!</p>
          <p className="print-contact">support@quantumpay.gh | +233 30 123 4567</p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessModal;
